import React, { useReducer, useEffect, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";

// Components
import ErrorBoundary from "../../shared/components/errors/ErrorBoundary";
import NetworkStatusIndicator from "../../shared/components/feedback/NetworkStatusIndicator";
import FileUploadSection from "../../features/data-upload/components/FileUploadSection";
import DelimiterSection from "../../features/data-upload/components/DelimiterSection";
import ColumnMappingSection from "../../features/data-upload/components/ColumnMappingSection";
import DataPreviewSection from "../../features/data-upload/components/DataPreviewSection";
import RestartButton from "../../shared/components/buttons/RestartButton";
import SaveButton from "../../shared/components/buttons/SaveButton";
import SubmitButton from "../../shared/components/buttons/SubmitButton";
import FileNameModal from "../../shared/components/modals/FileNameModal";
import ProgressTracker from "../../shared/components/feedback/ProgressTracker";
import WorkflowStepper from "../../shared/components/layout/WorkflowStepper";
import ErrorMessage from "../../shared/components/feedback/ErrorMessage";
import SuccessMessage from "../../shared/components/feedback/SuccessMessage";
import { UpgradeModal } from "../../features/subscription/components";

// Hooks
import { useFileUpload } from "../../shared/hooks/useFileUpload";
import { useColumnMapping } from "../../shared/hooks/useColumnMapping";
import { useNetworkStatus } from "../../shared/hooks/useNetworkStatus";
import { useQuotaCheck } from "../../features/subscription/hooks/useQuotaCheck";
import { useKeepAlive } from "@/core/services/keepalive";
import { useFileProcessing } from "@/features/data-upload/hooks/useFileProcessing";
import { useDataUpload } from "@/features/data-upload/hooks/useDataUpload";
import { useStepSave } from '@/shared/save/useStepSave';
import { useLeaveStepCleanup } from '@/shared/save/useLeaveStepCleanup';
import { createBackendFileSaveSource } from '@/features/data-upload/save/BackendFileSaveSource';
import type { SaveSource } from '@/shared/save/SaveSource';
import { useFileSizeLimit } from "@/hooks/useFileSizeLimit";
import { checkFile } from "../../features/data-upload/utils/fileGuards";
import { usePublishChatContext } from "@/features/chatbot/usePublishChatContext";
import { computeDataProfile } from "@/features/chatbot/computeDataProfile";
import type { ChatField } from "@/features/chatbot/types";

// State Management
import { uploadUIReducer, initialUIState } from "@/features/data-upload/reducers/uploadUIReducer";
import { mapBackendError } from "@/features/data-upload/utils/backendErrorMapper";
import InfoTooltip from "@/shared/components/forms/InfoTooltip";

// Types and Constants
import { 
  DEFAULT_TIMEZONE, 
  DEFAULT_HEADER_OPTION, 
  SCROLL_DELAY_MS,
  WORKFLOW_STEPS
} from "@/features/data-upload/types/dataUpload.types";


const RohdatenLadenRefactored: React.FC = () => {
  const { t } = useTranslation('dataUpload');

  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================
  
  // UI State with reducer
  const [uiState, dispatch] = useReducer(uploadUIReducer, initialUIState);

  // External hooks
  const { selectedFiles, firstTwoLines, fileInputRef, handleFileChange, resetFiles, removeFile } = useFileUpload();
  const {
    selectedColumn1, selectedColumn1Index, selectedColumn2, selectedColumn2Index,
    selectedColumn3, selectedColumn3Index, hasSeparateDateAndTime,
    setSelectedColumn1, setSelectedColumn1Index, setSelectedColumn2, setSelectedColumn2Index,
    setSelectedColumn3, setSelectedColumn3Index, setHasSeparateDateAndTime, resetColumnMappings
  } = useColumnMapping();
  const { isOnline } = useNetworkStatus();
  const { canUpload, checkStorage, refreshUsage } = useQuotaCheck();

  // Local state for form data (not UI state)
  const [delimiter, setDelimiter] = React.useState("");
  const [selectedTimezone, setSelectedTimezone] = React.useState(DEFAULT_TIMEZONE);
  const [valueColumnName, setValueColumnName] = React.useState("");
  const [customDateFormat, setCustomDateFormat] = React.useState("");
  const [showCustomFormat, setShowCustomFormat] = React.useState(false);
  const [radioHasHeader, setRadioHasHeader] = React.useState(DEFAULT_HEADER_OPTION);
  const [manualColumnSelection, setManualColumnSelection] = React.useState(false);

  // Keepalive hook
  useKeepAlive(uiState.isLoading);

  // File size limit based on subscription plan
  const { maxFileSizeBytes, formattedMaxSize } = useFileSizeLimit();

  // ============================================================================
  // CUSTOM HOOKS
  // ============================================================================

  // File Processing hook
  const fileProcessing = useFileProcessing({
    radioHasHeader,
    delimiter,
    manualColumnSelection,
    setSelectedColumn1,
    setSelectedColumn1Index,
    setSelectedColumn2,
    setSelectedColumn2Index,
    setSelectedColumn3,
    setSelectedColumn3Index,
    setHasSeparateDateAndTime
  });

  const { 
    headers, setHeaders, 
    tableData, setTableData, 
    processedTableData, setProcessedTableData,
    reDetectColumns, processFile, validateFiles, resetProcessing 
  } = fileProcessing;

  // Data Upload hook
  const dataUpload = useDataUpload({
    isOnline,
    selectedFiles,
    delimiter,
    radioHasHeader,
    selectedTimezone,
    hasSeparateDateAndTime,
    valueColumnName,
    selectedColumn1,
    selectedColumn1Index,
    selectedColumn2,
    selectedColumn2Index,
    selectedColumn3,
    selectedColumn3Index,
    customDateFormat,
    canUpload,
    checkStorage,
    refreshUsage,
    onError: (
      payload: string | { message: string; code?: string; suggestion?: string },
      persistent?: boolean,
    ) => {
      if (typeof payload === 'string') {
        dispatch({ type: 'SET_ERROR', payload: { message: payload, persistent } });
      } else {
        dispatch({
          type: 'SET_ERROR',
          payload: { ...payload, persistent },
        });
      }
    },
    onSuccess: (msg) => dispatch({ type: 'SET_SUCCESS', payload: msg }),
    onValidationError: (errors) => dispatch({ type: 'SET_COLUMN_ERRORS', payload: errors }),
    onUpgradeRequired: (message, resourceType) => dispatch({ 
      type: 'SET_UPGRADE_INFO', 
      payload: { message, resourceType } 
    }),
    onStartProcessing: () => dispatch({ type: 'START_PROCESSING' }),
    onStopProcessing: () => dispatch({ type: 'STOP_PROCESSING' }),
    setProcessedTableData
  });

  const { 
    processedFileIds,
    originalFileNames, standardProgress,
    uploadFiles, resetUpload
  } = dataUpload;

  // Live-data refs so the single stable SaveSource always reads current state.
  const fileIdsRef = useRef(processedFileIds);
  fileIdsRef.current = processedFileIds;
  const tableRef = useRef(processedTableData);
  tableRef.current = processedTableData;

  const buildInfoParams = () => ({
    originalFileNames,
    delimiter,
    radioHasHeader,
    selectedTimezone,
    hasSeparateDateAndTime,
    selectedColumn1,
    selectedColumn2,
    selectedColumn3,
    valueColumnName,
    customDateFormat,
  });
  const infoParamsRef = useRef(buildInfoParams);
  infoParamsRef.current = buildInfoParams;

  const saveSourceRef = useRef<SaveSource>(undefined);
  if (!saveSourceRef.current) {
    saveSourceRef.current = createBackendFileSaveSource({
      getFileIds: () => fileIdsRef.current,
      hasRows: () => tableRef.current.length > 0,
      getInfoFileParams: () => infoParamsRef.current(),
      t,
    });
  }
  const saveSource = saveSourceRef.current;

  const stepSave = useStepSave({
    source: saveSource,
    defaultName: (originalFileNames[0]?.replace(/\.[^.]+$/, '')) ?? 'processed_data',
    messages: {
      noData: t('errorMessages.noDataToSave'),
      nameRequired: t('errorMessages.fileNameRequired'),
      success: t('successMessages.downloadStarted'),
      genericError: t('errorMessages.unknownError'),
    },
  });

  // Delete backend files when leaving the step (24h scheduler is the backstop).
  useLeaveStepCleanup(saveSource.cleanup);

  // Bridge orchestrator status into the page's existing error/success UI.
  useEffect(() => {
    if (stepSave.error) {
      dispatch({ type: 'SET_ERROR', payload: { message: stepSave.error } });
    }
  }, [stepSave.error]);
  useEffect(() => {
    if (stepSave.success) {
      dispatch({ type: 'SET_SUCCESS', payload: stepSave.success });
      dispatch({ type: 'MARK_STEP_COMPLETED', payload: 5 });
    }
  }, [stepSave.success]);

  // ============================================================================
  // CHAT CONTEXT
  // ============================================================================

  const chatContext = useMemo(() => {
    const fields: ChatField[] = [];
    if (delimiter) fields.push({ label: 'Trennzeichen', value: delimiter });
    if (selectedTimezone) fields.push({ label: 'Zeitzone', value: selectedTimezone });
    if (valueColumnName) fields.push({ label: 'Wertspalte', value: valueColumnName });
    fields.push({ label: 'Kopfzeile vorhanden', value: radioHasHeader ? 'ja' : 'nein' });
    const dataProfile = tableData?.length
      ? computeDataProfile(tableData, headers ?? [], 0)
      : undefined;
    return { fields, dataProfile };
  }, [delimiter, selectedTimezone, valueColumnName, radioHasHeader, tableData, headers]);

  usePublishChatContext(chatContext);

  // ============================================================================
  // MEMOIZED VALUES
  // ============================================================================

  // Workflow steps with translations
  const workflowSteps = useMemo(() =>
    WORKFLOW_STEPS.map(step => ({
      id: step.id,
      label: t(step.label),
      description: step.description ? t(step.description) : undefined
    })), [t]);

  // ============================================================================
  // CALLBACKS
  // ============================================================================

  // Clear validation errors when user changes selection
  const clearValidationErrors = useCallback(() => {
    dispatch({ type: 'CLEAR_COLUMN_ERRORS' });
  }, []);

  // Re-detect columns with success message
  const handleReDetectColumns = useCallback(() => {
    if (reDetectColumns()) {
      dispatch({ type: 'SET_SUCCESS', payload: t('successMessages.columnsDetected') });
    }
  }, [reDetectColumns, t]);

  // Handle file input with error handling
  const handleFileInputChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    const allowedExtensions = ['.csv', '.txt'];

    // Validate ALL files synchronously BEFORE doing anything else
    for (const file of files) {
      const check = checkFile(file, allowedExtensions, maxFileSizeBytes);
      if (check.kind === 'bad_extension') {
        dispatch({
          type: 'SET_ERROR',
          payload: t('errorMessages.fileExtensionNotAllowed', {
            name: file.name,
            extension: check.extension,
            allowed: allowedExtensions.join(', '),
          }),
        });
        if (event.target) event.target.value = '';
        return;
      }
      if (check.kind === 'too_large') {
        dispatch({
          type: 'SET_ERROR',
          payload: t('errorMessages.fileSizeExceeded', {
            name: file.name,
            size: (check.sizeBytes / (1024 * 1024)).toFixed(2),
            maxSize: formattedMaxSize,
          }),
        });
        if (event.target) event.target.value = '';
        return;
      }
      if (check.kind === 'empty') {
        dispatch({
          type: 'SET_ERROR',
          payload: t('errorMessages.fileEmpty', { name: file.name }),
        });
        if (event.target) event.target.value = '';
        return;
      }
    }

    try {
      // Clear preview data when files change
      if (tableData.length > 0) {
        setTableData([]);
        setHeaders([]);
        dispatch({ type: 'SHOW_ACTION_BUTTONS', payload: false });
      }

      handleFileChange(event);
      dispatch({ type: 'SHOW_DELIMITER_INPUT', payload: true });
      dispatch({ type: 'MARK_STEP_COMPLETED', payload: 1 });
      dispatch({ type: 'SET_STEP', payload: 2 });
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : t('errorMessages.unknownError');
      dispatch({ type: 'SET_ERROR', payload: errorMsg });
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [tableData, setTableData, setHeaders, handleFileChange, t, maxFileSizeBytes, formattedMaxSize]);

  // Process files with delimiter
  const processFilesWithDelimiter = useCallback(async () => {
    try {
      // Start loading state IMMEDIATELY so the spinner covers BOTH validateFiles
      // (slow file read for delimiter detection) and processFile (chunk-by-chunk
      // parse) — not just the brief processFile phase.
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_LOADING_MESSAGE', payload: t('loadingMessages.parsingLargeFile') });

      const validation = await validateFiles(selectedFiles, delimiter);

      if (!validation.valid) {
        dispatch({ type: 'SET_ERROR', payload: validation.error || 'Validation error' });
        return;
      }

      const firstFile = selectedFiles[0];
      const fileData = await processFile(firstFile, 0);

      dispatch({ type: 'SHOW_DELIMITER_INPUT', payload: false });
      setTableData(fileData);
      dispatch({ type: 'SHOW_ACTION_BUTTONS', payload: true });
      dispatch({ type: 'SET_SUCCESS', payload: t('successMessages.fileLoaded') });
      dispatch({ type: 'MARK_STEP_COMPLETED', payload: 2 });
      dispatch({ type: 'SET_STEP', payload: 3 });
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : t('errorMessages.unknownError');
      dispatch({ type: 'SET_ERROR', payload: errorMsg });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
      dispatch({ type: 'SET_LOADING_MESSAGE', payload: null });
    }
  }, [selectedFiles, delimiter, validateFiles, processFile, setTableData, t]);

  // Server upload
  const serverConnect = useCallback(async () => {
    const success = await uploadFiles();
    if (success) {
      dispatch({ type: 'MARK_STEP_COMPLETED', payload: 3 });
      dispatch({ type: 'MARK_STEP_COMPLETED', payload: 4 });
      dispatch({ type: 'SET_STEP', payload: 5 });
    }
  }, [uploadFiles]);

  // Reset state
  const resetState = useCallback(() => {
    resetFiles();
    resetColumnMappings();
    resetProcessing();
    resetUpload();
    setDelimiter("");
    setValueColumnName("");
    setCustomDateFormat("");
    setShowCustomFormat(false);
    setSelectedTimezone(DEFAULT_TIMEZONE);
    setRadioHasHeader(DEFAULT_HEADER_OPTION);
    setManualColumnSelection(false);
    dispatch({ type: 'RESET_UI' });
    // Clear the actual <input type="file"> DOM element so the browser fires a
    // change event even when the user re-selects the same filename.
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    dispatch({ type: 'SET_SUCCESS', payload: t('successMessages.restartSuccess') });
  }, [resetFiles, resetColumnMappings, resetProcessing, resetUpload, t]);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Scroll effect
  useEffect(() => {
    if (uiState.shouldScrollToBottom || tableData.length > 0) {
      const scrollTimer = setTimeout(() => {
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: 'smooth'
        });
        if (uiState.shouldScrollToBottom) {
          dispatch({ type: 'SET_SCROLL_BOTTOM', payload: false });
        }
      }, uiState.shouldScrollToBottom ? 0 : SCROLL_DELAY_MS);
      
      return () => clearTimeout(scrollTimer);
    }
  }, [uiState.shouldScrollToBottom, tableData]);

  // Update workflow step based on current state
  useEffect(() => {
    if (selectedFiles.length > 0 && !uiState.showDelimiterInput && !tableData.length) {
      dispatch({ type: 'SET_STEP', payload: 2 });
    } else if (tableData.length > 0 && !processedTableData.length) {
      dispatch({ type: 'SET_STEP', payload: 3 });
    } else if (processedTableData.length > 0) {
      dispatch({ type: 'SET_STEP', payload: 5 });
    }
  }, [selectedFiles, uiState.showDelimiterInput, tableData, processedTableData]);

  // Disable page scrolling when loading
  useEffect(() => {
    if (uiState.isLoading && standardProgress) {
      const previousBodyOverflow = document.body.style.overflow;
      const previousHtmlOverflow = document.documentElement.style.overflow;
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = previousBodyOverflow;
        document.documentElement.style.overflow = previousHtmlOverflow;
      };
    }
  }, [uiState.isLoading, standardProgress]);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <ErrorBoundary>
      <div className="page-container">
        {/* Message Container */}
        <div className="messages-container">
          <div className="messages-wrapper">
            {uiState.errorMessage && (() => {
              const mapped = mapBackendError({
                code: uiState.errorCode ?? undefined,
                message: uiState.errorMessage,
              });
              const displayMessage = mapped.translationKey
                ? t(mapped.translationKey, { defaultValue: mapped.fallbackMessage })
                : (uiState.errorSuggestion ?? mapped.fallbackMessage);
              return (
                <ErrorMessage
                  message={displayMessage}
                  onClose={() => dispatch({ type: 'SET_ERROR', payload: null })}
                  duration={uiState.errorPersistent ? 0 : 5000}
                />
              );
            })()}
            <SuccessMessage 
              message={uiState.successMessage} 
              onClose={() => dispatch({ type: 'SET_SUCCESS', payload: null })} 
              duration={5000}
            />
          </div>
        </div>
        
        <NetworkStatusIndicator />
        
        <div className="main-container relative">
          {/* Workflow Stepper */}
          <WorkflowStepper
            steps={workflowSteps}
            currentStep={uiState.currentStep}
            completedSteps={uiState.completedSteps}
          />
          
          <div className="card max-w-7xl mx-auto bg-card p-8 shadow-lg rounded-lg">
            {/* ProgressTracker overlay */}
            {uiState.isLoading && standardProgress && (
              <div className="fixed inset-0 bg-background/95 z-[9999] flex items-center justify-center overflow-hidden">
                <div className="w-full max-w-lg mx-4 sm:mx-auto p-4 sm:p-6">
                  <ProgressTracker
                    progress={standardProgress}
                    className="my-4 sm:my-8"
                  />
                </div>
              </div>
            )}
            
            {/* File Upload Section */}
            {uiState.showFileUpload && (
              <h2 className="text-base sm:text-lg font-semibold text-foreground mb-4 flex items-center">
                {t('navBar.rohdatenLaden', { ns: 'navigation' })}
                <InfoTooltip text={t('tooltips.standardisierungSection')} width="w-96" />
              </h2>
            )}
            {uiState.showFileUpload && (
              <FileUploadSection
                onFileChange={handleFileInputChange}
                fileInputRef={fileInputRef}
                firstTwoLines={firstTwoLines}
                selectedFiles={selectedFiles}
                onRemoveFile={removeFile}
                onClearAll={resetFiles}
              />
            )}

            {/* Delimiter Section */}
            <DelimiterSection
              showDelimiterInput={uiState.showDelimiterInput}
              delimiter={delimiter}
              setDelimiter={setDelimiter}
              onProcessFiles={processFilesWithDelimiter}
              radioHasHeader={radioHasHeader}
              setRadioHasHeader={setRadioHasHeader}
            />

            {/* Parsing Loading Indicator */}
            {uiState.isLoading && uiState.loadingMessage && (
              <div className="flex items-center gap-3 p-3 bg-accent border border-primary/20 rounded-lg my-3">
                <svg className="animate-spin h-5 w-5 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-sm text-accent-foreground">{uiState.loadingMessage}</span>
              </div>
            )}

            {/* Data Preview Section */}
            <DataPreviewSection
              tableData={tableData}
              processedTableData={processedTableData}
              isLoading={uiState.isLoading && !standardProgress}
            />

            {/* Column Mapping Section */}
            {tableData.length > 0 && (
              <ColumnMappingSection
                headers={headers}
                selectedColumn1={selectedColumn1}
                selectedColumn1Index={selectedColumn1Index}
                selectedColumn2={selectedColumn2}
                selectedColumn2Index={selectedColumn2Index}
                selectedColumn3={selectedColumn3}
                selectedColumn3Index={selectedColumn3Index}
                hasSeparateDateAndTime={hasSeparateDateAndTime}
                selectedTimezone={selectedTimezone}
                valueColumnName={valueColumnName}
                showCustomFormat={showCustomFormat}
                customDateFormat={customDateFormat}
                columnValidationErrors={uiState.columnValidationErrors}
                manualColumnSelection={manualColumnSelection}
                onColumnChange={clearValidationErrors}
                onReDetectColumns={handleReDetectColumns}
                setSelectedColumn1={setSelectedColumn1}
                setSelectedColumn1Index={setSelectedColumn1Index}
                setSelectedColumn2={setSelectedColumn2}
                setSelectedColumn2Index={setSelectedColumn2Index}
                setSelectedColumn3={setSelectedColumn3}
                setSelectedColumn3Index={setSelectedColumn3Index}
                setSelectedTimezone={setSelectedTimezone}
                setValueColumnName={setValueColumnName}
                setShowCustomFormat={setShowCustomFormat}
                setCustomDateFormat={setCustomDateFormat}
                setManualColumnSelection={setManualColumnSelection}
                setHasSeparateDateAndTime={setHasSeparateDateAndTime}
              />
            )}

            {/* Required Field Legend */}
            {tableData.length > 0 && (
              <div className="mt-6 text-sm text-muted-foreground">
                <span className="text-red-500 font-medium">*</span> {t('fieldLegend.required')}
              </div>
            )}

            {/* Action Buttons */}
            {uiState.showActionButtons && (
              <div className="button-container">
                <SubmitButton
                  onClick={() => {
                    dispatch({ type: 'SET_SCROLL_BOTTOM', payload: true });
                    serverConnect();
                    dispatch({ type: 'MARK_STEP_COMPLETED', payload: 3 });
                    dispatch({ type: 'SET_STEP', payload: 4 });
                  }}
                  disabled={uiState.isLoading || !isOnline}
                >
                  {uiState.isLoading ? t('buttons.processing') : t('buttons.process')}
                </SubmitButton>

                {stepSave.canSave && (
                  <SaveButton
                    onSave={stepSave.openSaveModal}
                    iconSize={24}
                    disabled={uiState.isLoading || stepSave.isSaving}
                  >
                    {t('buttons.download')}
                  </SaveButton>
                )}
                <RestartButton
                  onRestart={resetState}
                  iconSize={24}
                  disabled={uiState.isLoading}
                />
                <FileNameModal
                  isOpen={stepSave.isModalOpen}
                  originalFileName={originalFileNames.length > 0 ? originalFileNames[0] : ""}
                  originalFileNames={originalFileNames}
                  saveDataName={stepSave.saveDataName}
                  setSaveDataName={stepSave.setSaveDataName}
                  onClose={stepSave.onCancel}
                  onSave={stepSave.onConfirm}
                  setError={(msg: string) => console.error('FileNameModal error:', msg)}
                  isLoading={stepSave.isSaving}
                />
              </div>
            )}
          </div>
        </div>

        {/* Upgrade Modal */}
        <UpgradeModal
          isOpen={uiState.showUpgradeModal}
          onClose={() => dispatch({ type: 'SHOW_UPGRADE_MODAL', payload: false })}
          resourceType={uiState.upgradeResourceType}
          message={uiState.upgradeMessage}
        />
      </div>
    </ErrorBoundary>
  );
};

export default RohdatenLadenRefactored;
